import {
  Card,
  CardContent,
  FormControl,
  FormHelperText,
  Grid,
  InputProps,
  Stack,
} from "@mui/material";
import { FormikErrors, getIn } from "formik";
import _ from "lodash";
import moment, { Moment } from "moment";
import { ReactNode } from "react";

import { AppDateTimePicker } from "@/common/components/Datetime/AppDateTimePicker";
import GeolocationPointInput from "@/common/components/Geolocation/GeolocationPointInput";
import LatitudeInput from "@/common/components/Geolocation/LatitudeInput";
import LongitudeInput from "@/common/components/Geolocation/LongitudeInput";
import { DATETIME_FORMATS } from "@/common/constants/common";
import { MemoHelper, memoWithReversedArgs } from "@/common/helpers/memo";
import { ValidationHelper } from "@/common/validation";
import { GeneralGeolocationInputDto } from "@/core/api/generated/v1";

interface Props {
  value: GeneralGeolocationInputDto | undefined;
  action?: React.ReactNode & ReactNode;
  errors: FormikErrors<GeneralGeolocationInputDto> | undefined;
  inputsProps?: {
    /** Applied to all inputs */
    all?: {
      size?: InputProps["size"];
    };
  };
  onChange?: (value: GeneralGeolocationInputDto | undefined) => void;
}

export default memoWithReversedArgs(
  MemoHelper.arePropsEqual.factory<Props>({
    isExcludeFunctionProps: true,
    excludeProps: (t) => [t.inputsProps.$path],
  }),
  function GeneralGeolocationInput({ value, action, inputsProps, errors, onChange }: Props) {
    const generalError = _.isString(errors) ? errors?.toString() : undefined;
    const pointError = ValidationHelper.getFormikErrorsAsString(errors?.point, {
      isIncludeNested: false,
    });

    return (
      <Card variant='outlined'>
        <CardContent>
          <Stack direction='row' spacing={1}>
            <Stack direction='column' spacing={2} sx={{ flex: 1 }}>
              <Grid container spacing={2}>
                {/* Map */}
                <Grid size={{ md: 12, lg: 9 }}>
                  <GeolocationPointInput
                    value={value?.point}
                    onChange={(newValue) => {
                      onChange &&
                        onChange({
                          ...value,
                          point: newValue || undefined,
                        });
                    }}
                  />
                </Grid>

                {/* Inputs */}
                <Grid size={{ md: 12, lg: 3 }}>
                  <Grid container spacing={1}>
                    <Grid size={{ xxs: 12, md: 3, lg: 12 }}>
                      <LatitudeInput
                        error={Boolean(getIn(errors, "point.latitude"))}
                        helperText={getIn(errors, "point.latitude")}
                        required
                        fullWidth
                        label='Latitude'
                        margin='none'
                        variant='outlined'
                        size={inputsProps?.all?.size || "small"}
                        value={value?.point?.latitude ?? ""}
                        onChange={({ e, newValue }) => {
                          onChange &&
                            onChange({
                              ...value,
                              point: {
                                ...value?.point,
                                latitude: newValue ?? undefined,
                              },
                            });
                        }}
                      />
                    </Grid>

                    <Grid size={{ xxs: 12, md: 3, lg: 12 }}>
                      <LongitudeInput
                        error={Boolean(getIn(errors, "point.longitude"))}
                        helperText={getIn(errors, "point.longitude")}
                        required
                        fullWidth
                        label='Longitude'
                        margin='none'
                        variant='outlined'
                        size={inputsProps?.all?.size || "small"}
                        value={value?.point?.longitude ?? ""}
                        onChange={({ e, newValue }) => {
                          onChange &&
                            onChange({
                              ...value,
                              point: {
                                ...value?.point,
                                longitude: newValue ?? undefined,
                              },
                            });
                        }}
                      />
                    </Grid>

                    <Grid size={{ xxs: 12, md: 3, lg: 12 }}>
                      <FormControl fullWidth margin='none' error={Boolean(errors?.capturedAt)}>
                        <AppDateTimePicker
                          label='Captured at'
                          format={DATETIME_FORMATS.DISPLAY_DATETIME}
                          value={(value?.capturedAt && moment(value.capturedAt)) || null}
                          onChange={(newValue: Moment | null) => {
                            onChange &&
                              onChange({
                                ...value,
                                capturedAt: newValue?.format(),
                              });
                          }}
                          slotProps={{ textField: { size: inputsProps?.all?.size || "small" } }}
                        />
                        <FormHelperText>{errors?.capturedAt}</FormHelperText>
                      </FormControl>
                    </Grid>

                    <Grid size={{ xxs: 12, md: 3, lg: 12 }}>
                      <Stack
                        direction='row'
                        sx={{
                          height: "100%",
                          alignItems: "center",
                          justifyContent: { lg: "flex-end" },
                        }}
                      >
                        <Stack direction='column' sx={{ justifyContent: "flex-start" }}>
                          {action && action}
                        </Stack>
                      </Stack>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              {pointError && <FormHelperText error>{pointError}</FormHelperText>}
              {generalError && <FormHelperText error>{generalError}</FormHelperText>}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  },
);
